import React, { useState, useRef } from 'react';
import { PieChart, Pie, Cell, Tooltip } from 'recharts';
import { NoDataPage } from '../../../resources/components/NoDataPage';

// Custom tooltip component
const CustomTooltip = ({ active, payload, legendHover }: any) => {
  if ((active && payload && payload.length) || legendHover) {
    const data = legendHover || payload[0];

    const total = data.payload?.total || data.total || 1;

    return (
      <div
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          padding: '8px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="d-flex align-items-center">
          <div
            style={{
              width: 12,
              height: 12,
              backgroundColor: data.payload?.fill || data.fill,
              marginRight: 8,
              borderRadius: 2,
            }}
          />
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 12 }}>
              {data.name}: {`${((data.value / total) * 100).toFixed(2)}%`}
            </div>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export const AnalyticsOverviewPieChartWidget = ({
  data,
  colors,
  height = 120,
  width = 120,
}: {
  data: { name: string; value: number }[];
  colors: string[];
  height?: number;
  width?: number;
}) => {
  const [hoveredLegendItem, setHoveredLegendItem] = useState<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const total = data.reduce((sum, item) => sum + item.value, 0);

  // If no data or total is zero
  if (data.length === 0 || total === 0) {
    return (
      <div
        ref={containerRef}
        className="d-flex flex-column align-items-center justify-content-center w-100"
        style={{ height }}
      >
        <NoDataPage />
      </div>
    );
  }

  const cx = width / 2;
  const cy = height / 2;
  const innerRadius = Math.min(width, height) * 0.2;
  const outerRadius = Math.min(width, height) * 0.3;

  const enhancedData = data.map((item, index) => ({
    ...item,
    fill: colors[index % colors.length],
    total,
  }));

  const handleLegendMouseEnter = (item: any, index: number) => {
    setHoveredLegendItem({
      name: item.name,
      value: item.value,
      fill: colors[index % colors.length],
      total,
    });
  };

  const handleLegendMouseLeave = () => {
    setHoveredLegendItem(null);
  };

  return (
    <div
      ref={containerRef}
      className="d-flex flex-column align-items-center w-100"
      style={{ position: 'relative' }}
    >
      {/* Tooltip */}
      {hoveredLegendItem && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            pointerEvents: 'none',
          }}
        >
          <CustomTooltip legendHover={hoveredLegendItem} />
        </div>
      )}

      {/* Pie Chart */}
      <div className="d-flex justify-content-center w-100">
        <PieChart
          width={width}
          height={height}
          style={{ position: 'relative', zIndex: 1 }}
        >
          <Pie
            data={enhancedData}
            cx={cx}
            cy={cy}
            innerRadius={innerRadius}
            outerRadius={outerRadius}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={0}
            startAngle={90}
            endAngle={450}
            labelLine={false}
            stroke="none"
          >
            {enhancedData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={colors[index % colors.length]}
                stroke="none"
              />
            ))}
          </Pie>
          <Tooltip
            content={<CustomTooltip />}
            position={{ x: 100, y: 40 }}
            wrapperStyle={{
              transform: 'translateX(100%)',
              zIndex: 9999,
              pointerEvents: 'none',
            }}
          />
        </PieChart>
      </div>

      {/* Legend */}
      <div
        className="d-flex justify-content-start flex-wrap mt-2"
        style={{ maxWidth: width * 2 }}
      >
        {data.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="d-flex align-items-center mx-2 mb-1"
            onMouseEnter={() => handleLegendMouseEnter(entry, index)}
            onMouseLeave={handleLegendMouseLeave}
            style={{ cursor: 'pointer' }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                backgroundColor: colors[index % colors.length],
                marginRight: 4,
                borderRadius: 2,
              }}
            />
            <span style={{ fontSize: 10, color: '#555' }}>{entry.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
